import { AxiosInstance } from 'axios';
import { injectable } from 'inversify';
import BaseService from '@core/services/base';
import Config from '@core/config';
import HttpClient from '@core/HttpClient';
import container from '@core/di';
import {
  Evaluation,
  AbilitiesNote,
  AdditionalCriteriaNote,
  EvaluationReviewStatus,
} from '@shared/models/assessmentdetails';
import { AgeLevelType } from '@shared/models/evaluations';
import { AssessmentFunctionContent } from '@shared/models/assessment-function-evaluation';
import {
  AssessmentHistory,
  BrainFunctionBatchHistory,
  ClinicalCriteriaBatchHistory,
} from '@shared/models/assesmentnotes';
import { AdditionalCriteriaType } from '@shared/models/clinicalcriteria';

export enum NoteUpdateAction {
  Delete = 'Delete',
  Restore = 'Restore',
  Edit = 'Edit',
}

@injectable()
export default class AssessmentsEvaluationsService extends BaseService {
  static diToken = Symbol('assessments-evaluations-service');
  private httpClient: AxiosInstance;

  constructor() {
    super({ domainArea: 'assessments' });
    this.httpClient = container.get<HttpClient>(HttpClient.diToken).getInstance();
  }

  async getMatrixAssessments() {
    const config = container.get<Config>(Config.diToken);
    const { clientApiURL } = config.get();

    const { data } = await this.httpClient({
      method: 'get',
      baseURL: `${clientApiURL.origin}${clientApiURL.pathname}`,
      url: 'assessments/matrix',
    });

    return data;
  }

  async getClinicalCriteria() {
    const config = container.get<Config>(Config.diToken);
    const { clientApiURL } = config.get();

    const { data } = await this.httpClient({
      method: 'get',
      baseURL: `${clientApiURL.origin}${clientApiURL.pathname}`,
      url: 'clinical-criteria',
    });

    return data;
  }

  async getEvaluations(assessmentId: number) {
    const config = container.get<Config>(Config.diToken);
    const { clientApiURL } = config.get();

    const { data } = await this.httpClient({
      method: 'get',
      baseURL: `${clientApiURL.origin}${clientApiURL.pathname}`,
      url: `assessments/${assessmentId}`,
    });

    return data;
  }

  async getCurrentClinicalCriteria(assessmentId: number): Promise<AdditionalCriteriaType[]> {
    const config = container.get<Config>(Config.diToken);
    const { clientApiURL } = config.get();

    const { data } = await this.httpClient({
      method: 'get',
      baseURL: `${clientApiURL.origin}${clientApiURL.pathname}`,
      url: `assessments/${assessmentId}/clinical-criteria`,
    });

    return data;
  }

  async getBrainFunctionHistory(
    childId: number,
    assessmentId: number,
    brainFunctionId: number,
  ): Promise<AssessmentHistory> {
    const { data } = await this.httpClient.get(
      this.getURL(`${assessmentId}/children/${childId}/brain-functions/${brainFunctionId}/history`),
    );

    return data;
  }

  async getAdditionalCriteriaHistory(
    childId: number,
    assessmentId: number,
    clinicalCriterionId: number,
  ): Promise<AssessmentHistory> {
    const { data } = await this.httpClient.get(
      this.getURL(
        `${assessmentId}/children/${childId}/clinical-criteria/${clinicalCriterionId}/history`,
      ),
    );

    return data;
  }

  async getBrainFunctionBatchHistory(
    childId: number,
    assessmentId: number,
  ): Promise<BrainFunctionBatchHistory[]> {
    const { data } = await this.httpClient.get(
      this.getURL(`${assessmentId}/children/${childId}/brain-functions/batch-history`),
    );

    return data;
  }

  async getClinicalCriteriaBatchHistory(
    childId: number,
    assessmentId: number,
  ): Promise<ClinicalCriteriaBatchHistory[]> {
    const { data } = await this.httpClient.get(
      this.getURL(`${assessmentId}/children/${childId}/clinical-criteria/batch-history`),
    );

    return data;
  }

  async addAbilitiesNote(assessmentId: number, note: AbilitiesNote) {
    const config = container.get<Config>(Config.diToken);
    const { clientApiURL } = config.get();

    const { data } = await this.httpClient({
      method: 'post',
      baseURL: `${clientApiURL.origin}${clientApiURL.pathname}`,
      url: `assessments/${assessmentId}/brain-functions/${note.brainFunctionId}/note`,
      data: { text: note.text },
    });

    return data;
  }

  async updateAbilitiesNote(
    assessmentId: number,
    brainFunctionId: number,
    noteId: number,
    action: NoteUpdateAction,
    text?: string,
  ) {
    const config = container.get<Config>(Config.diToken);
    const { clientApiURL } = config.get();

    const { data } = await this.httpClient({
      method: 'put',
      baseURL: `${clientApiURL.origin}${clientApiURL.pathname}`,
      url: `assessments/${assessmentId}/brain-functions/${brainFunctionId}/note/${noteId}`,
      data: { updateAction: action, ...(text && action === NoteUpdateAction.Edit ? { text } : {}) },
    });

    return data;
  }

  async addAdditionalCriteriaNote(assessmentId: number, note: AdditionalCriteriaNote) {
    const config = container.get<Config>(Config.diToken);
    const { clientApiURL } = config.get();

    const { data } = await this.httpClient({
      method: 'post',
      baseURL: `${clientApiURL.origin}${clientApiURL.pathname}`,
      url: `assessments/${assessmentId}/clinical-criteria/${note.clinicalCriterionId}/note`,
      data: { text: note.text },
    });

    return data;
  }

  async updateAdditionalCriteriaNote(
    assessmentId: number,
    clinicalCriterionId: number,
    noteId: number,
    action: NoteUpdateAction,
    text?: string,
  ) {
    const config = container.get<Config>(Config.diToken);
    const { clientApiURL } = config.get();

    const { data } = await this.httpClient({
      method: 'put',
      baseURL: `${clientApiURL.origin}${clientApiURL.pathname}`,
      url: `assessments/${assessmentId}/clinical-criteria/${clinicalCriterionId}/note/${noteId}`,
      data: { updateAction: action, ...(text && action === NoteUpdateAction.Edit ? { text } : {}) },
    });

    return data;
  }

  async editEvaluationRating(
    clientPrimaryAccountId: number,
    childId: number,
    brainFunctionEvaluations: { brainFunctionEvaluations: Evaluation[] },
  ) {
    const config = container.get<Config>(Config.diToken);
    const { clientApiURL } = config.get();

    const { data } = await this.httpClient({
      method: 'put',
      baseURL: `${clientApiURL.origin}${clientApiURL.pathname}`,
      url: `client-primary-accounts/${clientPrimaryAccountId}/children/${childId}/assessments/current/brain-functions`,
      data: brainFunctionEvaluations,
    });

    return data;
  }

  async reviewBrainFunctions(
    clientPrimaryAccountId: number,
    childId: number,
    brainFunctionIds: number[],
    reviewStatus: EvaluationReviewStatus,
  ) {
    const config = container.get<Config>(Config.diToken);
    const { clientApiURL } = config.get();

    const { data } = await this.httpClient({
      method: 'put',
      baseURL: `${clientApiURL.origin}${clientApiURL.pathname}`,
      url: `client-primary-accounts/${clientPrimaryAccountId}/children/${childId}/assessments/current/brain-functions/review`,
      data: {
        brainFunctionsEvaluationReviewStatus: brainFunctionIds.map(id => ({
          reviewStatus,
          brainFunctionId: id,
        })),
      },
    });

    return data;
  }

  async reviewAdditionalCriteria(
    clientPrimaryAccountId: number,
    childId: number,
    clinicalCriterionIds: number[],
    reviewStatus: EvaluationReviewStatus,
  ) {
    const config = container.get<Config>(Config.diToken);
    const { clientApiURL } = config.get();

    const { data } = await this.httpClient({
      method: 'put',
      baseURL: `${clientApiURL.origin}${clientApiURL.pathname}`,
      url: `client-primary-accounts/${clientPrimaryAccountId}/children/${childId}/assessments/current/clinical-criteria/review`,
      data: {
        clinicalCriteriaEvaluationReviewStatus: clinicalCriterionIds.map(id => ({
          reviewStatus,
          clinicalCriterionId: id,
        })),
      },
    });

    return data;
  }

  async markDomainAsNotYetAchieved(
    clientPrimaryAccountId: number,
    childId: number,
    brainDomain: string,
    currentBrainLevelId: number,
  ) {
    const config = container.get<Config>(Config.diToken);
    const { clientApiURL } = config.get();

    const { data } = await this.httpClient({
      method: 'put',
      baseURL: `${clientApiURL.origin}${clientApiURL.pathname}`,
      url: `client-primary-accounts/${clientPrimaryAccountId}/children/${childId}/assessments/current/mark-not-yet-achieved`,
      data: {
        brainDomain,
        currentBrainLevelId,
      },
    });

    return data;
  }

  async editChildAge(clientPrimaryAccountId: number, childId: number, ageAtLevel: AgeLevelType) {
    const config = container.get<Config>(Config.diToken);
    const { clientApiURL } = config.get();

    const { data } = await this.httpClient({
      method: 'put',
      baseURL: `${clientApiURL.origin}${clientApiURL.pathname}`,
      url: `client-primary-accounts/${clientPrimaryAccountId}/children/${childId}/assessments/current/child-age-level-eight`,
      data: ageAtLevel,
    });

    return data;
  }

  async editAdditionalCriteria(clientPrimaryAccountId: number, childId: number, ids: any) {
    const config = container.get<Config>(Config.diToken);
    const { clientApiURL } = config.get();

    const { data } = await this.httpClient({
      method: 'put',
      baseURL: `${clientApiURL.origin}${clientApiURL.pathname}`,
      url: `client-primary-accounts/${clientPrimaryAccountId}/children/${childId}/assessments/current/clinical-criteria`,
      data: {
        clinicalCriterionOptionIds: ids,
      },
    });

    return data;
  }

  async createNewAssessment(clientPrimaryAccountId: number, childId: number) {
    const config = container.get<Config>(Config.diToken);
    const { clientApiURL } = config.get();

    const { data } = await this.httpClient({
      method: 'post',
      baseURL: `${clientApiURL.origin}${clientApiURL.pathname}`,
      url: `client-primary-accounts/${clientPrimaryAccountId}/children/${childId}/assessments`,
    });

    return data;
  }

  async approveAssessment(
    clientPrimaryAccountId: number,
    childId: number,
    chronologicalAgeBasis: 'submission' | 'approval',
  ) {
    const config = container.get<Config>(Config.diToken);
    const { clientApiURL } = config.get();
    const { data } = await this.httpClient({
      method: 'put',
      baseURL: `${clientApiURL.origin}${clientApiURL.pathname}`,
      url: `client-primary-accounts/${clientPrimaryAccountId}/children/${childId}/assessments/current/approve`,
      data: { chronologicalAgeBasis },
    });

    return data;
  }

  async submitAssessment(clientPrimaryAccountId: number, childId: number) {
    const config = container.get<Config>(Config.diToken);
    const { clientApiURL } = config.get();
    const { data } = await this.httpClient({
      method: 'put',
      baseURL: `${clientApiURL.origin}${clientApiURL.pathname}`,
      url: `client-primary-accounts/${clientPrimaryAccountId}/children/${childId}/assessments/current/submit`,
    });

    return data;
  }

  /** Submit subsequent assessment created by clinician, review all functions and approve */
  async finalizeAssessment(clientPrimaryAccountId: number, childId: number) {
    const config = container.get<Config>(Config.diToken);
    const { clientApiURL } = config.get();
    const { data } = await this.httpClient({
      method: 'put',
      baseURL: `${clientApiURL.origin}${clientApiURL.pathname}`,
      url: `client-primary-accounts/${clientPrimaryAccountId}/children/${childId}/assessments/current/finalize`,
    });

    return data;
  }

  async getAssessmentStatus(clientPrimaryAccountId: number, childId: number) {
    const config = container.get<Config>(Config.diToken);
    const { clientApiURL } = config.get();
    const { data } = await this.httpClient({
      method: 'get',
      baseURL: `${clientApiURL.origin}${clientApiURL.pathname}`,
      url: `client-primary-accounts/${clientPrimaryAccountId}/children/${childId}/assessments/current-status`,
    });

    return data;
  }

  async assignAssessmentToClinician(
    assessmentId: number,
    clientPrimaryAccountId: number,
    childId: number,
  ) {
    const config = container.get<Config>(Config.diToken);
    const { clientApiURL } = config.get();
    const { data } = await this.httpClient({
      method: 'put',
      baseURL: `${clientApiURL.origin}${clientApiURL.pathname}`,
      url: `client-primary-accounts/${clientPrimaryAccountId}/children/${childId}/assessments/${assessmentId}/assign`,
    });

    return data;
  }

  async flagBrainFunctionEvaluation(
    assessmentId: number,
    brainFunctionId: number,
    isFlagged: boolean,
  ) {
    const { data } = await this.httpClient.patch(
      this.getURL(`${assessmentId}/brain-functions/${brainFunctionId}/flag`),
      { isFlagged },
    );

    return data;
  }

  async flagClinicalCriterionEvaluation(
    assessmentId: number,
    clinicalCriterionId: number,
    isFlagged: boolean,
  ) {
    const { data } = await this.httpClient.patch(
      this.getURL(`${assessmentId}/clinical-criteria/${clinicalCriterionId}/flag`),
      { isFlagged },
    );

    return data;
  }

  async getContent(brainFunctionId: number) {
    const config = container.get<Config>(Config.diToken);
    const { clientApiURL } = config.get();

    const { data } = await this.httpClient({
      method: 'get',
      baseURL: `${clientApiURL.origin}${clientApiURL.pathname}`,
      url: `assessments/brain-functions/${brainFunctionId}/content`,
    });

    return data as AssessmentFunctionContent;
  }
}
